import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./MockExam.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, QBankCode } from "../utils/qbankProfile";
import QBankQuestionImage, { QBankImage } from "../components/QBankQuestionImage";

interface Option {
    label: string;
    text: string;
}

interface Question {
    id: string;
    collection_id?: string | null;
    collection?: {
        id: string;
        title: string;
        year: number;
        month: number;
    } | null;
    question_text: string;
    options: Option[];
    specialty: string;
    difficulty: string;
    user_answer: string | null;
    flagged: boolean;
    question_image?: QBankImage | null;
}

interface Exam {
    id: string;
    status: string;
    started_at: string;
    total_questions: number;
    questions: Question[];
}

const MockExam: React.FC = () => {
    const { bank: bankParam, examId } = useParams<{ bank: string; examId: string }>();
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";
    const { user } = useAuthContext();
    const navigate = useNavigate();

    const [exam, setExam] = useState<Exam | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [reviewFlaggedMode, setReviewFlaggedMode] = useState(false);

    const startTimeRef = useRef<number>(Date.now());
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    // Timer
    useEffect(() => {
        const interval = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Load exam
    useEffect(() => {
        if (user?.id && examId) {
            loadExam();
        }
    }, [user, examId, bank]);

    const loadExam = async () => {
        if (!user?.id || !examId) return;

        try {
            const res = await authenticatedFetch(`${apiUrl}/api/mock-exams/${examId}?user_id=${user.id}&bank=${bank}`);
            if (res.ok) {
                const data = await res.json();
                if (data.status === "completed") {
                    navigate(`/qbank/${bank}/mock-exams/${examId}/results`);
                    return;
                }
                setExam(data);
            } else {
                navigate(`/qbank/${bank}/mock-exams`);
            }
        } catch (err) {
            console.error("Failed to load exam", err);
            navigate(`/qbank/${bank}/mock-exams`);
        } finally {
            setLoading(false);
        }
    };

    const saveAnswer = useCallback(
        async (questionId: string, answer: string | null, flagged?: boolean) => {
            if (!user?.id || !examId) return;

            try {
                await authenticatedFetch(`${apiUrl}/api/mock-exams/${examId}/answers/${questionId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        bank_code: bank,
                        answer: answer,
                        flagged: flagged,
                    }),
                });
            } catch (err) {
                console.error("Failed to save answer", err);
            }
        },
        [apiUrl, bank, examId, user]
    );

    const handleSelectOption = (label: string) => {
        if (!exam) return;

        const currentQ = exam.questions[currentIndex];
        const newAnswer = currentQ.user_answer === label ? null : label;

        // Update local state
        const updatedQuestions = [...exam.questions];
        updatedQuestions[currentIndex] = { ...currentQ, user_answer: newAnswer };
        setExam({ ...exam, questions: updatedQuestions });

        // Save to server
        saveAnswer(currentQ.id, newAnswer);
    };

    const handleToggleFlag = () => {
        if (!exam) return;

        const currentQ = exam.questions[currentIndex];
        const newFlagged = !currentQ.flagged;

        // Update local state
        const updatedQuestions = [...exam.questions];
        updatedQuestions[currentIndex] = { ...currentQ, flagged: newFlagged };
        setExam({ ...exam, questions: updatedQuestions });

        // Save to server
        saveAnswer(currentQ.id, currentQ.user_answer, newFlagged);
    };

    const handleSubmitExam = async () => {
        if (!user?.id || !examId) return;

        setSubmitting(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/mock-exams/${examId}/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    bank_code: bank,
                    time_taken_seconds: elapsedSeconds,
                }),
            });

            if (res.ok) {
                navigate(`/qbank/${bank}/mock-exams/${examId}/results`);
            } else {
                const error = await res.json();
                (window as any).showNotification?.(error.error || "Failed to submit", "error");
            }
        } catch (err) {
            console.error("Failed to submit exam", err);
            (window as any).showNotification?.("Failed to submit exam", "error");
        } finally {
            setSubmitting(false);
            setShowConfirmModal(false);
        }
    };

    const formatTime = (seconds: number) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
        }
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    if (loading) {
        return (
            <div className={styles.pageContainer}>
                <Header />
                <main className={styles.mainContent}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner} />
                        <p>Loading exam...</p>
                    </div>
                </main>
            </div>
        );
    }

    if (!exam) {
        return null;
    }

    const currentQuestion = exam.questions[currentIndex];
    const answeredCount = exam.questions.filter((q) => q.user_answer).length;
    const flaggedCount = exam.questions.filter((q) => q.flagged).length;
    const unansweredCount = exam.total_questions - answeredCount;

    // Flagged question navigation helpers
    const flaggedIndices = exam.questions
        .map((q, i) => (q.flagged ? i : -1))
        .filter((i) => i !== -1);

    const currentFlaggedPosition = flaggedIndices.indexOf(currentIndex);
    const hasPrevFlagged = reviewFlaggedMode && currentFlaggedPosition > 0;
    const hasNextFlagged = reviewFlaggedMode && currentFlaggedPosition < flaggedIndices.length - 1;

    const goToPrevFlagged = () => {
        if (hasPrevFlagged) {
            setCurrentIndex(flaggedIndices[currentFlaggedPosition - 1]);
        }
    };

    const goToNextFlagged = () => {
        if (hasNextFlagged) {
            setCurrentIndex(flaggedIndices[currentFlaggedPosition + 1]);
        }
    };

    const toggleReviewFlaggedMode = () => {
        if (!reviewFlaggedMode && flaggedIndices.length > 0) {
            // Entering review mode - jump to first flagged question
            setCurrentIndex(flaggedIndices[0]);
        }
        setReviewFlaggedMode(!reviewFlaggedMode);
    };

    return (
        <div className={styles.pageContainer}>
            <Header />

            <main className={styles.mainContent}>
                {/* Question Panel */}
                <div className={styles.questionPanel}>
                    <div className={styles.questionHeader}>
                        <span className={styles.questionNumber}>
                            Question <strong>{currentIndex + 1}</strong> of {exam.total_questions}
                            {reviewFlaggedMode && (
                                <span className={styles.reviewModeBadge}>
                                    🚩 Reviewing Flagged ({currentFlaggedPosition + 1}/{flaggedCount})
                                </span>
                            )}
                        </span>
                        <div className={styles.questionBadges}>
                            {currentQuestion.collection?.title && (
                                <span className={styles.collectionBadge}>
                                    {currentQuestion.collection.title}
                                </span>
                            )}
                            <span className={styles.specialtyBadge}>{currentQuestion.specialty}</span>
                        </div>
                    </div>

                    <p className={styles.questionText}>{currentQuestion.question_text}</p>
                    <QBankQuestionImage image={currentQuestion.question_image} />

                    <div className={styles.optionsList}>
                        {currentQuestion.options.map((option) => (
                            <button
                                key={option.label}
                                className={`${styles.optionButton} ${currentQuestion.user_answer === option.label
                                    ? styles.selected
                                    : ""
                                    }`}
                                onClick={() => handleSelectOption(option.label)}
                            >
                                <span className={styles.optionLabel}>{option.label}</span>
                                <span className={styles.optionText}>{option.text}</span>
                            </button>
                        ))}
                    </div>

                    <div className={styles.questionActions}>
                        <button
                            className={`${styles.flagButton} ${currentQuestion.flagged ? styles.flagged : ""
                                }`}
                            onClick={handleToggleFlag}
                        >
                            {currentQuestion.flagged ? "🚩 Flagged" : "🏳️ Flag for Review"}
                        </button>
                    </div>

                    <div className={styles.navigationControls}>
                        {reviewFlaggedMode ? (
                            <>
                                <button
                                    className={styles.navButton}
                                    onClick={goToPrevFlagged}
                                    disabled={!hasPrevFlagged}
                                >
                                    ← Prev Flagged
                                </button>
                                <button
                                    className={`${styles.navButton} ${styles.exitReview}`}
                                    onClick={toggleReviewFlaggedMode}
                                >
                                    Exit Review
                                </button>
                                <button
                                    className={`${styles.navButton} ${styles.primary}`}
                                    onClick={goToNextFlagged}
                                    disabled={!hasNextFlagged}
                                >
                                    Next Flagged →
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className={styles.navButton}
                                    onClick={() => setCurrentIndex((i) => i - 1)}
                                    disabled={currentIndex === 0}
                                >
                                    ← Previous
                                </button>
                                {currentIndex < exam.total_questions - 1 ? (
                                    <button
                                        className={`${styles.navButton} ${styles.primary}`}
                                        onClick={() => setCurrentIndex((i) => i + 1)}
                                    >
                                        Next →
                                    </button>
                                ) : (
                                    <button
                                        className={`${styles.navButton} ${styles.primary}`}
                                        onClick={() => setShowConfirmModal(true)}
                                    >
                                        Finish Exam →
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <aside className={styles.sidebar}>
                    <div className={styles.timerCard}>
                        <h3>Time Elapsed</h3>
                        <div className={styles.timerValue}>{formatTime(elapsedSeconds)}</div>
                    </div>

                    <div className={styles.progressCard}>
                        <div className={styles.progressHeader}>
                            <h3>Progress</h3>
                            <span className={styles.progressStats}>
                                {answeredCount}/{exam.total_questions}
                            </span>
                        </div>
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{
                                    width: `${(answeredCount / exam.total_questions) * 100}%`,
                                }}
                            />
                        </div>

                        <div className={styles.questionGrid}>
                            {exam.questions.map((q, i) => (
                                <button
                                    key={q.id}
                                    className={`${styles.gridItem} ${i === currentIndex ? styles.current : ""
                                        } ${q.user_answer ? styles.answered : ""} ${q.flagged ? styles.flagged : ""
                                        } ${reviewFlaggedMode && !q.flagged ? styles.dimmed : ""}`}
                                    onClick={() => setCurrentIndex(i)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>

                        {flaggedCount > 0 && (
                            <button
                                className={`${styles.reviewFlaggedButton} ${reviewFlaggedMode ? styles.active : ""}`}
                                onClick={toggleReviewFlaggedMode}
                            >
                                {reviewFlaggedMode ? "🚩 Exit Flagged Review" : `🚩 Review Flagged (${flaggedCount})`}
                            </button>
                        )}
                    </div>

                    <div className={styles.submitCard}>
                        <button
                            className={styles.submitButton}
                            onClick={() => setShowConfirmModal(true)}
                            disabled={submitting}
                        >
                            Submit Exam
                        </button>
                        {unansweredCount > 0 && (
                            <p className={styles.unansweredWarning}>
                                {unansweredCount} question{unansweredCount > 1 ? "s" : ""} unanswered
                            </p>
                        )}
                    </div>
                </aside>
            </main>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h2>Submit Exam?</h2>
                        <p>
                            You have answered {answeredCount} of {exam.total_questions} questions.
                            {unansweredCount > 0 && (
                                <>
                                    <br />
                                    <strong>{unansweredCount}</strong> question
                                    {unansweredCount > 1 ? "s are" : " is"} still unanswered.
                                </>
                            )}
                            {flaggedCount > 0 && (
                                <>
                                    <br />
                                    <strong>{flaggedCount}</strong> question
                                    {flaggedCount > 1 ? "s are" : " is"} flagged for review.
                                </>
                            )}
                        </p>
                        <div className={styles.modalActions}>
                            <button
                                className={`${styles.modalButton} ${styles.modalCancel}`}
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Continue Exam
                            </button>
                            <button
                                className={`${styles.modalButton} ${styles.modalConfirm}`}
                                onClick={handleSubmitExam}
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : "Submit Now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MockExam;
